import React, { useState, useEffect, useRef } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import apiClient from "../../../services/api";
import { REGION_OPTIONS } from "../../../constants/grants.constants";

interface EditOperatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  operatorId?: string | null;
}

interface OperatorFormData {
  name: string;
  shortName: string;
  region: string;
  district: string;
  website: string;
  email: string;
  phone: string;
  addressLocality: string;
  addressPostalCode: string;
  addressStreet: string;
  addressStreetNumber: string;
  isActive: boolean;
}

const EditOperatorModal: React.FC<EditOperatorModalProps> = ({
  isOpen,
  onClose,
  operatorId,
}) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<OperatorFormData>({
    name: "",
    shortName: "",
    region: "",
    district: "",
    website: "",
    email: "",
    phone: "",
    addressLocality: "",
    addressPostalCode: "",
    addressStreet: "",
    addressStreetNumber: "",
    isActive: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [globalError, setGlobalError] = useState<string | null>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);

  // Fetch operator data if editing
  const { data: operatorData, isLoading: isLoadingOperator } = useQuery({
    queryKey: ["grants-operator", operatorId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/grants/operators/${operatorId}`);
      return data;
    },
    enabled: !!operatorId && isOpen,
  });

  // Populate form when operator data is loaded
  useEffect(() => {
    if (operatorData) {
      setFormData({
        name: operatorData.name || "",
        shortName: operatorData.shortName || "",
        region: operatorData.region || "",
        district: operatorData.district || "",
        website: operatorData.website || "",
        email: operatorData.email || "",
        phone: operatorData.phone || "",
        addressLocality: operatorData.address?.locality || "",
        addressPostalCode: operatorData.address?.postalCode || "",
        addressStreet: operatorData.address?.street || "",
        addressStreetNumber: operatorData.address?.streetNumber || "",
        isActive: operatorData.isActive ?? true,
      });
    }
  }, [operatorData]);

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      await apiClient.post("/grants/operators", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grants-operators"] });
      onClose();
      resetForm();
    },
    onError: (error: any) => {
      if (error.response?.status >= 500) {
        setGlobalError('Blad serwera. Sprobuj ponownie pozniej.');
      } else if (!error.response) {
        setGlobalError('Blad polaczenia. Sprawdz polaczenie z internetem.');
      } else if (error.response?.data?.details) {
        const newErrors: Record<string, string> = {};
        error.response.data.details.forEach(
          (detail: { field: string; message: string }) => {
            newErrors[detail.field] = detail.message;
          }
        );
        setErrors(newErrors);
      } else {
        setGlobalError('Wystapil nieoczekiwany blad. Sprobuj ponownie.');
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      await apiClient.put(`/grants/operators/${operatorId}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grants-operators"] });
      queryClient.invalidateQueries({ queryKey: ["grants-operator", operatorId] });
      onClose();
      resetForm();
    },
    onError: (error: any) => {
      if (error.response?.status >= 500) {
        setGlobalError('Blad serwera. Sprobuj ponownie pozniej.');
      } else if (!error.response) {
        setGlobalError('Blad polaczenia. Sprawdz polaczenie z internetem.');
      } else if (error.response?.data?.details) {
        const newErrors: Record<string, string> = {};
        error.response.data.details.forEach(
          (detail: { field: string; message: string }) => {
            newErrors[detail.field] = detail.message;
          }
        );
        setErrors(newErrors);
      } else {
        setGlobalError('Wystapil nieoczekiwany blad. Sprobuj ponownie.');
      }
    },
  });

  const resetForm = () => {
    setFormData({
      name: "",
      shortName: "",
      region: "",
      district: "",
      website: "",
      email: "",
      phone: "",
      addressLocality: "",
      addressPostalCode: "",
      addressStreet: "",
      addressStreetNumber: "",
      isActive: true,
    });
    setErrors({});
    setGlobalError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setGlobalError(null);

    // Build address object
    const address: any = {};
    if (formData.addressLocality) address.locality = formData.addressLocality;
    if (formData.addressPostalCode) address.postalCode = formData.addressPostalCode;
    if (formData.addressStreet) address.street = formData.addressStreet;
    if (formData.addressStreetNumber) address.streetNumber = formData.addressStreetNumber;

    const payload = {
      name: formData.name,
      shortName: formData.shortName || null,
      region: formData.region || null,
      district: formData.district || null,
      website: formData.website || null,
      email: formData.email || null,
      phone: formData.phone || null,
      address: Object.keys(address).length > 0 ? address : null,
      isActive: formData.isActive,
    };

    if (operatorId) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleClose = () => {
    if (!createMutation.isPending && !updateMutation.isPending) {
      resetForm();
      onClose();
    }
  };

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !createMutation.isPending && !updateMutation.isPending) {
        resetForm();
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, createMutation.isPending, updateMutation.isPending]);

  // Auto-focus first input when modal opens
  useEffect(() => {
    if (isOpen && !isLoadingOperator) {
      setTimeout(() => {
        firstInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, isLoadingOperator]);

  if (!isOpen) return null;

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 my-8">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h2 className="text-lg font-medium text-neutral-900">
            {operatorId ? "Edytuj operatora" : "Dodaj nowego operatora"}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {globalError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md" role="alert">
              <p className="text-sm text-red-700">{globalError}</p>
            </div>
          )}

          {isLoadingOperator ? (
            <div className="text-center py-8" role="status" aria-live="polite">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-neutral-600">Ładowanie...</p>
            </div>
          ) : (
            <>
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Nazwa <span className="text-red-600">*</span>
                </label>
                <input
                  ref={firstInputRef}
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 ${
                    errors.name ? "border-red-500" : "border-neutral-300"
                  }`}
                  placeholder="Powiatowy Urząd Pracy w Warszawie"
                  maxLength={255}
                  required
                />
                {errors.name && (
                  <p className="mt-1 text-xs text-red-600">{errors.name}</p>
                )}
              </div>

              {/* Short Name */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Skrócona nazwa
                </label>
                <input
                  type="text"
                  value={formData.shortName}
                  onChange={(e) =>
                    setFormData({ ...formData, shortName: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                  placeholder="PUP Warszawa"
                  maxLength={100}
                />
              </div>

              {/* Region */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Województwo
                </label>
                <select
                  value={formData.region}
                  onChange={(e) =>
                    setFormData({ ...formData, region: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Wybierz województwo</option>
                  {REGION_OPTIONS.map((region) => (
                    <option key={region} value={region}>
                      {region}
                    </option>
                  ))}
                </select>
              </div>

              {/* District */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Powiat
                </label>
                <input
                  type="text"
                  value={formData.district}
                  onChange={(e) =>
                    setFormData({ ...formData, district: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                  placeholder="m.st. Warszawa"
                  maxLength={50}
                />
                <p className="mt-1 text-xs text-neutral-500">
                  Dla PUP-ów (opcjonalnie)
                </p>
              </div>

              {/* Website */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Strona WWW
                </label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) =>
                    setFormData({ ...formData, website: e.target.value })
                  }
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 ${
                    errors.website ? "border-red-500" : "border-neutral-300"
                  }`}
                  placeholder="https://pup.warszawa.pl"
                  maxLength={500}
                />
                {errors.website && (
                  <p className="mt-1 text-xs text-red-600">{errors.website}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 ${
                    errors.email ? "border-red-500" : "border-neutral-300"
                  }`}
                  placeholder="kontakt@pup.warszawa.pl"
                  maxLength={255}
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-red-600">{errors.email}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Telefon
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                  placeholder="22 123 45 67"
                  maxLength={20}
                />
              </div>

              {/* Address Section */}
              <div className="border-t border-neutral-200 pt-4">
                <h3 className="text-sm font-medium text-neutral-900 mb-3">
                  Adres
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  {/* Locality */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Miejscowość
                    </label>
                    <input
                      type="text"
                      value={formData.addressLocality}
                      onChange={(e) =>
                        setFormData({ ...formData, addressLocality: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                      placeholder="Warszawa"
                      maxLength={100}
                    />
                  </div>

                  {/* Postal Code */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Kod pocztowy
                    </label>
                    <input
                      type="text"
                      value={formData.addressPostalCode}
                      onChange={(e) =>
                        setFormData({ ...formData, addressPostalCode: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                      placeholder="00-123"
                      maxLength={10}
                    />
                  </div>

                  {/* Street */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Ulica
                    </label>
                    <input
                      type="text"
                      value={formData.addressStreet}
                      onChange={(e) =>
                        setFormData({ ...formData, addressStreet: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                      placeholder="ul. Marszałkowska"
                      maxLength={200}
                    />
                  </div>

                  {/* Street Number */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Numer
                    </label>
                    <input
                      type="text"
                      value={formData.addressStreetNumber}
                      onChange={(e) =>
                        setFormData({ ...formData, addressStreetNumber: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                      placeholder="123/45"
                      maxLength={20}
                    />
                  </div>
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center pt-4">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                  className="h-4 w-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
                />
                <label
                  htmlFor="isActive"
                  className="ml-2 text-sm text-neutral-700"
                >
                  Operator aktywny
                </label>
              </div>
            </>
          )}
        </form>

        <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-neutral-700 bg-neutral-100 rounded-md hover:bg-neutral-200 disabled:opacity-50"
          >
            Anuluj
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || isLoadingOperator}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading && (
              <div role="status" aria-live="polite">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span className="sr-only">Zapisywanie...</span>
              </div>
            )}
            {isLoading
              ? "Zapisywanie..."
              : operatorId
              ? "Zaktualizuj"
              : "Dodaj operatora"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditOperatorModal;
